import moment from 'moment'
import * as Yup from 'yup'

import FORMAT from './format'
import { isValidCPF } from './functions'

const ERRORS = {
  endDateBeforeStart: 'Anterior a inicial',
  endDateRequireStart: 'Obrigatório quando há final',
  invalidCpf: 'CPF Inválido',
  invalidDate: 'Data inválida',
  invalidProcess: 'Processo inválido',
  required: 'Campo obrigatório',
  startDateAfterEnd: 'Posterior a final',
  startDateRequireEnd: 'Obrigatório quando há inicial'
}

interface Types {
  cpf?: Yup.StringSchema<string, Object, string>
  string?: Yup.StringSchema<string, Object, string>
  process?: Yup.StringSchema<string, Object, string>
  number?: Yup.NumberSchema<number, Object, number>
  date?: Yup.AnySchema<any, Object, any>
  object?: Yup.AnySchema<any, Object, any>
}

const VALIDATIONS: Types = {
  cpf: Yup.string()
    .default('')
    .test('cpf', ERRORS.invalidCpf, (value) => isValidCPF(value))
    .max(14, ERRORS.invalidCpf),
  date: Yup.mixed().test('invalid-date', ERRORS.invalidDate, (date) => {
    const minYear = moment(date).isAfter('01/01/1500')
    const maxYear = moment(date).isBefore('01/01/3000')

    if (!date) {
      return true
    }

    if (minYear && maxYear && moment(date).isValid()) {
      return true
    }

    return false
  }),
  number: Yup.number(),
  // process: Yup.string()
  //   .default('')
  //   .test('invalid-process', ERRORS.invalidProcess, (val) => {
  //     // return FORMAT.unMask(val).length === 12
  //     return typeof val === 'number'
  //   }),
  string: Yup.string().default(''),
  object: Yup.object(),
}

const validateStartDateWithEnd = (end: string) =>
  Yup.mixed()
    .typeError(ERRORS.invalidDate)
    .required(ERRORS.required)
    .nullable()
    .concat(VALIDATIONS.date)
    .when(end, (endDate: string, schema: any) => {
      if (endDate && moment(endDate).isValid()) {
        // const date = new Date(new Date(endDate).toDateString())
        // date.setDate(date.getDate() + 1)
        // return schema.max(date, ERRORS.startDateAfterEnd)
        const { startDateAfterEnd } = ERRORS

        return schema.test('after', startDateAfterEnd, (startDate: any) => {
          // if (!startDate) return true
          return !moment(startDate).isAfter(endDate)
        })
      }

      return (
        schema
          // Essas duas linhas impedem o ".nullable().notRequired()"
          // funcionar no "dateRange("inicio","fim").notRequired/endNotRequired
          // e não parecem ser necessárias.
          // .typeError(ERRORS.invalidDate)
          // .required(ERRORS.required)
          .nullable()
      )
    })

const validateEndDateWithStart = (start: string) =>
  Yup.mixed()
    .typeError(ERRORS.invalidDate)
    .required(ERRORS.required)
    .nullable()
    .concat(VALIDATIONS.date)
    .when(start, (startDate: string, schema: any) => {
      if (startDate && moment(startDate).isValid()) {
        // const date = new Date(new Date(startDate).toDateString())
        const { endDateBeforeStart } = ERRORS

        return schema.test('before', endDateBeforeStart, (endDate: any) => {
          // if (!endDate) return true
          return !moment(endDate).isBefore(startDate)
        })
      }

      return (
        schema
          // Essas duas linhas impedem o ".nullable().notRequired()"
          // funcionar no "dateRange("inicio","fim").notRequired/endNotRequired
          // e não parecem ser necessárias.
          // .typeError(ERRORS.invalidDate)
          // .required(ERRORS.required)
          .nullable()
      )
    })

const requiredByDefault = (types: Types) => {
  let obj: Types = {}
  Object.keys(types).forEach((key) => {
    obj = { ...obj, [key]: types[key].required(ERRORS.required).nullable() }
  })
  return obj
}

const nullableTypes = (types: Types) => {
  let obj: Types = {}
  Object.keys(types).forEach((key) => {
    obj = { ...obj, [key]: types[key].nullable() }
  })
  return obj
}

const requiredWhen = (types: Types, field: string) => {
  let obj: Types = {}
  Object.keys(types).forEach((key) => {
    obj = {
      ...obj,
      [key]: types[key].when(field, (fieldValue: any) => {
        if (fieldValue) return types[key].required(ERRORS.required)
      })
    }
  })
  return obj
}

export default {
  ERRORS,
  dateRange: (start: string, end: string) => {
    return {
      endNotRequired: Yup.object().shape(
        {
          [end]: validateEndDateWithStart(start).nullable().notRequired(),
          [start]: validateStartDateWithEnd(end)
        },
        [[start, end]]
      ),
      notRequired: Yup.object().shape(
        {
          [end]: validateEndDateWithStart(start).nullable().notRequired(),
          [start]: validateStartDateWithEnd(end).nullable().notRequired()
        },
        [[start, end]]
      ),
      oneRequireAnother: Yup.object().shape(
        {
          [end]: validateEndDateWithStart(start)
            .nullable()
            .notRequired()
            .when(start, (fieldValue: any, schema) => {
              if (fieldValue) {
                return schema.required(ERRORS.startDateRequireEnd)
              }
              return schema
            }),
          [start]: validateStartDateWithEnd(end)
            .nullable()
            .notRequired()
            .nullable()
            .notRequired()
            .when(end, (fieldValue: any, schema) => {
              if (fieldValue) {
                return schema.required(ERRORS.endDateRequireStart)
              }
              return schema
            })
        },
        [[start, end]]
      ),
      requireEndIfStart: Yup.object().shape(
        {
          [end]: validateEndDateWithStart(start)
            .nullable()
            .notRequired()
            .when(start, (fieldValue: any, schema) => {
              if (fieldValue) {
                return schema.required(ERRORS.startDateRequireEnd)
              }
              return schema
            }),
          [start]: validateStartDateWithEnd(end).nullable().notRequired()
        },
        [[start, end]]
      ),
      required: Yup.object().shape(
        {
          [end]: validateEndDateWithStart(start),
          [start]: validateStartDateWithEnd(end)
        },
        [[start, end]]
      ),
      object: Yup.object(),
    }
  },
  required: requiredByDefault(VALIDATIONS),
  requiredWhen: (field: string) => requiredWhen(VALIDATIONS, field),
  ...nullableTypes(VALIDATIONS)
}
